import { css } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';
import SPACING from '@zola/zola-ui/src/styles/emotion/spacing';
import type { WWeddingThemeCustomizationView } from '@zola/svc-web-api-ts-client';
import { MEDIA_QUERY } from '@zola/zola-ui/src/styles/emotion';

/**
 *
 * @param bgColor hex code string without '#'
 * @returns true if dark false if light color
 *
 * https://awik.io/determine-color-bright-dark-using-javascript/
 */
export const isDarkColor = (bgColor: string): boolean => {
  // If hex --> Convert it to RGB: http://gist.github.com/983661
  const color = +`0x${bgColor.replace(bgColor.length < 4 ? /./g : '', '$&$&')}`;
  // Variables for red, green, blue values
  // eslint-disable-next-line no-bitwise
  const r = color >> 16;
  // eslint-disable-next-line no-bitwise
  const g = (color >> 8) & 255;
  // eslint-disable-next-line no-bitwise
  const b = color & 255;

  // HSP (Highly Sensitive Poo) equation from http://alienryderflex.com/hsp.html
  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  // Using the HSP value, determine whether the color is light or dark
  return hsp < 127.5;
};

function hexToRgb(hex: string) {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const modifiedHex = hex.replace(shorthandRegex, (m, r, g, b) => {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(modifiedHex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function luminance(r: number, g: number, b: number) {
  const a = [r, g, b].map(val => {
    const v = val / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}
/**
 * https://dev.to/alvaromontoro/building-your-own-color-contrast-checker-4j7o
 * @param color1 hex color string prepended with '#'
 * @param color2 hex color string prepended with '#'
 * @returns true if color contrast passes AA-level large text WCAG standard
 */
export function getIsColorContrastSufficient(color1: string, color2: string): boolean {
  const color1rgb = hexToRgb(color1);
  const color2rgb = hexToRgb(color2);
  if (!color1rgb || !color2rgb) return false;
  const color1luminance = luminance(color1rgb?.r, color1rgb?.g, color1rgb?.b);
  const color2luminance = luminance(color2rgb?.r, color2rgb?.g, color2rgb?.b);
  const ratio =
    color1luminance > color2luminance
      ? (color2luminance + 0.05) / (color1luminance + 0.05)
      : (color1luminance + 0.05) / (color2luminance + 0.05);

  return ratio < 1 / 3;
}

export const getColorStyle = (color?: string | null): string =>
  color
    ? `
        color: #${color} !important;
      `
    : '';

export const getFontFamilyStyle = (fontFamily?: string | null): string =>
  fontFamily
    ? `
        font-family: ${fontFamily} !important;
      `
    : '';

export const getBackgroundColorStyle = (bgColor?: string | null): string =>
  bgColor
    ? `
    background: #${bgColor} !important;
  `
    : '';

export const getGenericOverride = (): SerializedStyles => {
  return css`
    #public-wrapper {
      .sticky-nav {
        #public-page-header h1 a {
          line-height: normal !important;
          padding-top: ${SPACING.SM} !important;
          padding-bottom: ${SPACING.SM} !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
        }

        .pages-nav-container .list-inline > li a {
          line-height: normal !important;
        }
      }

      #page-hidden-banner {
        p {
          font-family: 'proxima-nova', 'Helvetica', Arial, sans-serif !important;
          color: #ffffff !important;
          font-style: italic;
        }
      }

      #publish-website-banner {
        p,
        a {
          font-family: 'proxima-nova', 'Helvetica', Arial, sans-serif !important;
          color: #ffffff !important;
          letter-spacing: normal;
        }
      }

      .hero-wrapper .home-page-hero .homepage-header-text h1 {
        line-height: normal !important;
        padding-top: ${SPACING.SM} !important;
        padding-bottom: ${SPACING.SM} !important;
      }

      .hero-wrapper .home-page-hero.has-image .homepage-header-text h1 {
        color: #ffffff !important;
      }

      .section-subtitle {
        font-weight: normal !important;
      }

      #registry-zola h6 {
        font-family: 'proxima-nova', 'Helvetica', Arial, sans-serif !important;
      }

      .btn,
      .btn-primary {
        overflow-wrap: break-word !important;
        white-space: normal !important;
      }

      #rsvp-page-v2 {
        .form-group.attending {
          & > .row {
            display: flex;
            flex-flow: row;
            align-items: center;
          }
        }
      }
    }
  `;
};

export const getGlobalOverride = (backgroundColorOverride?: string): SerializedStyles | '' => {
  if (!backgroundColorOverride) return '';
  const bgStyle = getBackgroundColorStyle(backgroundColorOverride);
  return css`
    html {
      &#public-wedding-background {
        ${bgStyle}
      }

      &.public-wedding-page {
        ${bgStyle}

        body {
          ${bgStyle}
        }
      }
    }
  `;
};

export const getButtonOverride = (
  bodyColor?: string | null,
  backgroundColor?: string | null,
  bodyFont?: string | null,
  defaultValues?: WWeddingThemeCustomizationView
): SerializedStyles | null => {
  if (!(backgroundColor || bodyColor)) return null;

  const baseButtonStyles = `
    background-color: #${backgroundColor || defaultValues?.primary_background_color} !important;
    color: #${bodyColor || defaultValues?.body_font_color} !important;
    border-color: #${bodyColor || defaultValues?.body_font_color} !important;
  `;
  const activeButtonStyles = `
    background-color: #${bodyColor || defaultValues?.body_font_color} !important;
    color: #${backgroundColor || defaultValues?.primary_background_color} !important;
    border-color: #${bodyColor || defaultValues?.body_font_color} !important;
  `;
  const disabledButtonStyles = `
    background-color: #d9d9d9 !important;
    color: #505050 !important;
    border-color: #d9d9d9 !important;
  `;
  const buttonStyles = `
    ${getFontFamilyStyle(bodyFont)}
    ${baseButtonStyles}
    &:hover, &:active {
      ${activeButtonStyles}
    }
    &:disabled {
      ${disabledButtonStyles}
    }
  `;

  return css`
    #public-wrapper {
      .btn-primary {
        ${buttonStyles}
      }
      :not(#registry-zola) {
        &.btn {
          ${buttonStyles}
        }
      }
      div.btn-active {
        ${activeButtonStyles}
      }

      #rsvp-page-v2,
      .public-page-passcode {
        span .input-override {
          color: #fff !important;
          border: solid 1px #d9d9d9 !important;
          background-color: #ffffff !important;
        }
        .form-group {
          &.attending,
          &.edit-guest-name {
            .btn {
              ${buttonStyles}
            }
            div.btn-active {
              ${activeButtonStyles}
            }
          }
        }
        :not(.scroll-select, .btn-boxed) {
          &.btn {
            ${buttonStyles}
          }
        }
        a.events-actions__link-next.events-actions__link--is-disabled {
          ${disabledButtonStyles}
          cursor: not-allowed;
          &:hover:not([disabled='disabled']) {
            ${disabledButtonStyles}
          }
        }
        a.events-actions__link-next {
          ${buttonStyles}
        }
        // INPUT OVERRIDES
        input,
        textarea {
          background-color: #ffffff !important;
          border-color: #000000 !important;
          color: #444444 !important;
          font-family: 'Cardo', serif;

          // Submit button on RSVP summary page
          &.btn {
            ${buttonStyles}
          }
          &div.btn-active {
            ${activeButtonStyles}
          }
        }
        input {
          &.form-control {
            background-color: #ffffff !important;
            border-color: #000000 !important;
            color: #444444 !important;
            font-family: 'Cardo', serif;
          }
          &.radio-inline &.input-override:hover {
            border-color: #444444;
          }
          &.radio-inline &.input-override {
            background-color: #ffffff;
          }
          &.radio-inline input[type='radio'] + &.input-override:before {
            background-color: #ffffff;
          }
          &.radio-inline input[type='radio']:checked + &.input-override:before {
            background-color: #444444;
          }
        }
        input:focus,
        textarea:focus {
          border-color: #444444;
        }
        input::placeholder,
        textarea::placeholder {
          color: #444444;
        }
        input:-ms-input-placeholder {
          color: #444444;
        }
        input::-ms-input-placeholder {
          color: #444444;
        }
        textarea:-ms-input-placeholder {
          color: #444444;
        }
        textarea::-ms-input-placeholder {
          color: #444444;
        }
        // DROPDOWN OVERRIDE
        span,
        .scroll-select-option,
        .scroll-select,
        select {
          border-color: #444444 !important;
          font-family: 'Cardo', serif;
          font-weight: 400;
        }
      }
    }
  `;
};

export const getIconStyling = (
  backgroundColor?: string | null,
  headerFontColor?: string | null,
  bannerShown?: boolean
): SerializedStyles => {
  const isDarkBackground = backgroundColor ? isDarkColor(backgroundColor) : false;
  const conditionalBgColor = isDarkBackground ? 'FFFFFF' : '21201F';
  const color = headerFontColor || conditionalBgColor;
  const iconStyle = getColorStyle(color);
  return css`
    #public-wrapper {
      // Cart
      i {
        &.zolaicon-cart {
          ${backgroundColor && iconStyle}

          ${MEDIA_QUERY.DESKTOP} {
            // if banner, change on desktop
            ${bannerShown && getColorStyle('21201F')}
          }
        }
      }
      // Hamburger and 'x' mobile menu
      .sticky-nav .nav-control {
        ${backgroundColor && iconStyle}
      }
    }
  `;
};

/**
 * Color accessibility overrides for standard, non-user customizable elements
 * (ie. attending/not attending label, zola-ui checkbox, input error messaging)
 * when background color has changed
 */
export const getConditionalColorOverride = (
  backgroundColor?: string | null,
  bodyFontColor?: string | null,
  defaultValues?: WWeddingThemeCustomizationView
): SerializedStyles => {
  const bgColor = backgroundColor
    ? `#${backgroundColor}`
    : `#${defaultValues?.primary_background_color}`;
  const isDarkBg = isDarkColor(bgColor);
  const getOverrideColor = () => (isDarkBg ? 'ffffff' : '21201f');

  const contrastsWithRed = getIsColorContrastSufficient(bgColor, '#c7133d');
  const errorMessagingStyle = getColorStyle(contrastsWithRed ? 'c7133d' : getOverrideColor());

  const contrastsWithGreen = getIsColorContrastSufficient(bgColor, '#4aa425');
  const attendingMessagingStyle = getColorStyle(contrastsWithGreen ? '4aa425' : getOverrideColor());

  const contrastsWithZuiBlue = getIsColorContrastSufficient(bgColor, '#0075ae');
  const zuiOverrideColor = contrastsWithZuiBlue ? '0075ae' : getOverrideColor();

  return css`
    #public-wrapper {
      #rsvp-page-v2,
      .public-page-passcode {
        .has-error .help-block {
          ${errorMessagingStyle}
        }
        input[type='checkbox']:checked + .input-override {
          background-color: #0075ae;
          border-color: ${zuiOverrideColor} !important;
          i {
            // checkbox icon
            color: #fff;
          }
        }
        .guest-rsvp-summary .rsvp-info .guest-rsvp-response {
          .declined {
            i {
              ${errorMessagingStyle}
            }
            ${errorMessagingStyle}
          }
          .attending {
            ${attendingMessagingStyle}
            .zolaicon-checkmark:before {
              ${attendingMessagingStyle}
            }
          }
        }
        .rsvp-guest-form
          .guest-rsvp-success-header
          .guest-rsvp-email-container
          .guest-rsvp-email
          .disclaimer
          a {
          ${getColorStyle(bodyFontColor || defaultValues?.body_font_color)}
          text-decoration: underline;
        }
      }
    }
  `;
};
